import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { LocaleProvider } from "@/i18n/LocaleContext";
import { localePath } from "@/i18n/translations";
import { privacyContent, privacyPath } from "@/i18n/privacy";
import useLenis from "@/hooks/useLenis";
import { Footer } from "@/components/Footer";

const ease = [0.76, 0, 0.24, 1];

const PrivacyContent = ({ locale }) => {
  const P = privacyContent[locale] || privacyContent.en;
  const home = localePath[locale] || "/";

  return (
    <main className="relative min-h-screen pt-32 pb-24" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-6">
        <Link
          to={home}
          data-testid="privacy-back"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-nowi-muted hover:text-nowi-venture transition-colors mb-14"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {P.back}
        </Link>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="font-playfair text-5xl md:text-7xl tracking-[-0.02em]"
        >
          {P.title}
        </motion.h1>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-nowi-muted">{P.updated}</p>
        <p className="mt-6 max-w-2xl text-nowi-muted text-sm md:text-base">{P.intro}</p>

        <div className="mt-14 border-t border-white/10">
          {P.sections.map((s, i) => (
            <section
              key={i}
              data-testid={`privacy-section-${i}`}
              className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-2 md:gap-8 py-8 border-b border-white/10"
            >
              <h2 className="font-playfair text-xl md:text-2xl text-nowi-text leading-snug">{s.h}</h2>
              <p className="text-nowi-muted text-sm md:text-base leading-relaxed">{s.b}</p>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
};

export const PrivacyPage = ({ locale = "en" }) => {
  useLenis();
  const P = privacyContent[locale] || privacyContent.en;
  return (
    <LocaleProvider locale={locale} seoTitle={`${P.title} — NOWI`} seoDescription={P.intro} canonicalPath={privacyPath(locale)}>
      <div className="App bg-nowi-bg text-nowi-text font-manrope">
        <div className="noise-overlay" />
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
            <Link to={localePath[locale] || "/"} className="font-syncopate font-bold text-lg tracking-[0.15em] text-nowi-text" data-testid="privacy-logo">
              NOWI<span className="text-nowi-tech">.</span>
            </Link>
          </div>
        </header>
        <PrivacyContent locale={locale} />
        <Footer />
      </div>
    </LocaleProvider>
  );
};
